import { useEffect } from 'react';
import { BRAND } from '../config/brand';

/**
 * Sets `document.title` for the lifetime of the calling component, then
 * restores the previous title on unmount.
 *
 * Pass the page-specific part — the hook tacks " · {brand}" on the end so
 * every tab still reads "Whatever · Safe and Sorted". Pass `null`/empty and
 * the title falls back to just the brand name.
 *
 * Why: AppShell uses this so every page that supplies a `title` prop gets a
 * unique <title> automatically. Without it, every authenticated screen
 * reported "Safe and Sorted" to screen readers, browser tabs, and
 * computer-use AI agents — a user with several tabs open couldn't tell them
 * apart, and an assistive-tech user landing on a new page had no anchor.
 *
 * Composing multi-part titles is the caller's job. Idiomatic format:
 *   "Sign in · Plot 17 Beechwood"  →  full tab reads "Sign in · Plot 17 Beechwood · Safe and Sorted"
 */
export function useDocumentTitle(name: string | null | undefined): void {
  useEffect(() => {
    const previous = document.title;
    const next = name && name.trim() ? `${name.trim()} · ${BRAND.name}` : BRAND.name;
    document.title = next;
    return () => {
      document.title = previous;
    };
  }, [name]);
}
